import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { services } from "../data/services";
import { mainCities } from "../data/cities";

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-zinc-950 text-white sticky top-0 z-50">
      {/* Top bar - Urgence */}
      <div className="bg-red-600 text-white py-2 px-4 text-center text-sm font-medium">
        <span className="inline-flex items-center gap-2">
          <span className="animate-pulse">●</span>
          URGENCE SERRURIER 24H/24 – Intervention rapide à Lieusaint et Sénart
        </span>
      </div>

      {/* Main header - increased height for larger logo */}
      <div className="container-custom">
        <div className="flex items-center justify-between h-[72px] md:h-[88px] lg:h-[100px]">
          {/* Logo - significantly larger: 50-70px */}
          <Link 
            to="/" 
            className="flex items-center flex-shrink-0"
            data-testid="header-logo"
          >
            <img 
              src="/serrurier-lieusaint-logo.jpg" 
              alt="Serrurier Lieusaint dépannage serrurerie 24h/24"
              className="h-[50px] sm:h-[56px] md:h-[64px] lg:h-[70px] w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            <Link 
              to="/" 
              className={`text-sm uppercase tracking-wide transition-colors ${isActive('/') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'}`}
              data-testid="nav-home"
            >
              Accueil
            </Link>

            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm uppercase tracking-wide text-white hover:text-yellow-400 transition-colors" data-testid="nav-services-dropdown">
                Services <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
                {services.map((service) => (
                  <DropdownMenuItem key={service.id} asChild>
                    <Link 
                      to={`/service/${service.slug}`}
                      className="text-white hover:text-yellow-400 hover:bg-zinc-800 cursor-pointer"
                      data-testid={`nav-service-${service.slug}`}
                    >
                      {service.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Villes Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm uppercase tracking-wide text-white hover:text-yellow-400 transition-colors" data-testid="nav-cities-dropdown">
                Zones <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-zinc-900 border-zinc-800 max-h-80 overflow-y-auto">
                {mainCities.map((city) => (
                  <DropdownMenuItem key={city.slug} asChild>
                    <Link 
                      to={`/serrurier/${city.slug}`}
                      className="text-white hover:text-yellow-400 hover:bg-zinc-800 cursor-pointer"
                      data-testid={`nav-city-${city.slug}`}
                    >
                      Serrurier {city.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <Link 
                    to="/zones-intervention"
                    className="text-yellow-400 hover:text-yellow-300 hover:bg-zinc-800 cursor-pointer font-medium"
                    data-testid="nav-all-cities"
                  >
                    Voir toutes les villes →
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link 
              to="/blog" 
              className={`text-sm uppercase tracking-wide transition-colors ${isActive('/blog') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'}`}
              data-testid="nav-blog"
            >
              Blog
            </Link>

            <Link 
              to="/contact" 
              className={`text-sm uppercase tracking-wide transition-colors ${isActive('/contact') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'}`}
              data-testid="nav-contact"
            >
              Contact
            </Link>
          </nav>

          {/* CTA Button */}
          <div className="flex items-center gap-4">
            <a href="tel:0668032561" data-testid="header-call-button">
              <Button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wide px-4 py-2 md:px-6 md:py-3 rounded-sm animate-pulse-glow">
                <Phone className="w-4 h-4 mr-2" />
                <span className="hidden md:inline">06 68 03 25 61</span>
                <span className="md:hidden">Appeler</span>
              </Button>
            </a>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-zinc-900 border-t border-zinc-800" data-testid="mobile-menu">
          <nav className="container-custom py-4 flex flex-col gap-2">
            <Link 
              to="/" 
              className="py-3 px-4 text-white hover:bg-zinc-800 rounded-sm uppercase tracking-wide"
              onClick={() => setMobileMenuOpen(false)}
            >
              Accueil
            </Link>
            
            <div className="py-3 px-4 text-zinc-400 text-sm uppercase tracking-wider">Services</div>
            {services.slice(0, 4).map((service) => (
              <Link 
                key={service.id}
                to={`/service/${service.slug}`}
                className="py-2 px-6 text-white hover:bg-zinc-800 rounded-sm text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                {service.name}
              </Link>
            ))}
            
            <div className="py-3 px-4 text-zinc-400 text-sm uppercase tracking-wider">Zones</div>
            {mainCities.slice(0, 4).map((city) => (
              <Link 
                key={city.slug}
                to={`/serrurier/${city.slug}`}
                className="py-2 px-6 text-white hover:bg-zinc-800 rounded-sm text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Serrurier {city.name}
              </Link>
            ))}
            <Link 
              to="/zones-intervention"
              className="py-2 px-6 text-yellow-400 hover:bg-zinc-800 rounded-sm text-sm"
              onClick={() => setMobileMenuOpen(false)}
            >
              Toutes les villes →
            </Link>

            <Link 
              to="/blog"
              className="py-3 px-4 text-white hover:bg-zinc-800 rounded-sm uppercase tracking-wide"
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </Link>

            <Link 
              to="/contact"
              className="py-3 px-4 text-white hover:bg-zinc-800 rounded-sm uppercase tracking-wide"
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
